import { addDaysIso, isoMonday, uniqueLessonDates } from '@/lib/date-utils'
import { buildBrockenUnits, weekdayOf, type BrockenLesson, type BrockenUnit, type WeekRankMode } from '@/utils/brockenUnits'

export type { WeekRankMode }

export type RankEntry = {
  teacherId: string
  rank: number
  teacherName: string
  teacherCode?: string
}

export type WeekPlanBlock = {
  id: string
  date?: string | null
  timeSlotIds: string[]
  blockName: string | null
  entries: RankEntry[]
}

export type SlotCol = { id: string; startTime: string }

export type WeekSlice = {
  monday: string
  label: string
  dates: string[]
}

export type DaySeg = {
  span: number
  empty: boolean
  seriesId: string
  colorKey: string
  label: string
  date: string
  entries: RankEntry[]
}

export type WeekDayCol = {
  weekday: number
  date: string
  heading: string
  sub: string
  off: boolean
  segs: DaySeg[]
}

export type WeekGrid = {
  monday: string
  label: string
  slots: SlotCol[]
  days: WeekDayCol[]
}

const WD_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

export function colorKeyOf(date: string, fingerprint: string): string {
  return `${weekdayOf(date)}:${fingerprint}`
}

export function seriesIdOf(date: string, fingerprint: string, mode: WeekRankMode): string {
  if (mode === 'each') return `${isoMonday(date)}:${weekdayOf(date)}:${fingerprint}`
  return `${weekdayOf(date)}:${fingerprint}`
}

export function orderedSlots(lessons: BrockenLesson[]): SlotCol[] {
  const map = new Map<string, string>()
  for (const l of lessons) {
    if (!l.timeSlotId) continue
    const t = l.startTime || ''
    const prev = map.get(l.timeSlotId)
    if (prev == null || t < prev) map.set(l.timeSlotId, t)
  }
  return [...map.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]))
    .map(([id, startTime]) => ({ id, startTime }))
}

function formatDayNum(iso: string): string {
  return String(new Date(iso + 'T00:00:00').getDate())
}

function monthShort(iso: string): string {
  return MONTH_SHORT[new Date(iso + 'T00:00:00').getMonth()] ?? ''
}

export function weekLabel(dates: string[]): string {
  if (dates.length === 0) return ''
  const first = dates[0]!
  const last = dates[dates.length - 1]!
  const a = formatDayNum(first)
  const b = formatDayNum(last)
  if (first.slice(0, 7) === last.slice(0, 7)) return `${a}.–${b}. ${monthShort(first)}`
  return `${a}. ${monthShort(first)} – ${b}. ${monthShort(last)}`
}

export function groupDatesByIsoWeek(dates: string[]): WeekSlice[] {
  const sorted = [...new Set(dates)].sort()
  const map = new Map<string, string[]>()
  for (const d of sorted) {
    const mon = isoMonday(d)
    const list = map.get(mon) ?? []
    list.push(d)
    map.set(mon, list)
  }
  return [...map.keys()].sort().map(monday => {
    const ds = map.get(monday) ?? []
    return { monday, dates: ds, label: weekLabel(ds) }
  })
}

function weekdaysToShow(datesInWeek: string[]): number[] {
  const set = new Set(datesInWeek.map(d => weekdayOf(d)))
  const cols = [1, 2, 3, 4, 5]
  if (set.has(6)) cols.push(6)
  if (set.has(0)) cols.push(0)
  return cols
}

function unitLabel(unit: BrockenUnit): string {
  const subj = unit.lessons.map(l => l.subjectName).find(Boolean) || ''
  if (unit.timeSlotIds.length >= 2) return subj ? `Doppel ${subj}` : 'Doppel'
  return subj || unit.shortLabel || 'Brocken'
}

function matchBlock(blocks: WeekPlanBlock[], unit: BrockenUnit): WeekPlanBlock | undefined {
  const fp = unit.fingerprint
  return blocks.find(b =>
    b.date === unit.date && [...b.timeSlotIds].sort().join(',') === fp,
  ) || blocks.find(b => b.id === `block-brocken-${unit.date}-${fp}`)
}

function segsForDate(
  date: string,
  slots: SlotCol[],
  units: BrockenUnit[],
  blocks: WeekPlanBlock[],
  mode: WeekRankMode,
): DaySeg[] {
  const dayUnits = units.filter(u => u.date === date)
  const slotIndex = new Map(slots.map((s, i) => [s.id, i]))
  const covered = new Set<number>()
  const segs: DaySeg[] = []
  for (let i = 0; i < slots.length; i++) {
    if (covered.has(i)) continue
    const unit = dayUnits.find(u => slotIndex.get(u.timeSlotIds[0]!) === i)
    if (!unit) {
      segs.push({ span: 1, empty: true, seriesId: '', colorKey: '', label: '', date, entries: [] })
      covered.add(i)
      continue
    }
    const indices = unit.timeSlotIds.map(id => slotIndex.get(id)).filter((n): n is number => n != null).sort((a, b) => a - b)
    let span = 1
    for (let k = 1; k < indices.length; k++) {
      if (indices[k] === indices[0]! + span) span++
      else break
    }
    for (let k = 0; k < span; k++) covered.add(i + k)
    const block = matchBlock(blocks, unit)
    segs.push({
      span,
      empty: false,
      seriesId: seriesIdOf(date, unit.fingerprint, mode),
      colorKey: colorKeyOf(date, unit.fingerprint),
      label: unitLabel(unit),
      date,
      entries: [...(block?.entries ?? [])].sort((a, b) => a.rank - b.rank),
    })
  }
  return segs
}

export function buildWeekGrid(
  lessons: BrockenLesson[],
  blocks: WeekPlanBlock[],
  week: WeekSlice,
  mode: WeekRankMode,
  keepTogether = true,
): WeekGrid {
  const weekLessons = lessons.filter(l => l.date && isoMonday(l.date) === week.monday)
  const slots = orderedSlots(weekLessons.length ? weekLessons : lessons)
  const units = buildBrockenUnits(weekLessons, { keepTogether })
  const cols = weekdaysToShow(week.dates)
  const days: WeekDayCol[] = cols.map(wd => {
    const date = addDaysIso(week.monday, wd === 0 ? 6 : wd - 1)
    const has = week.dates.includes(date)
    return {
      weekday: wd,
      date,
      heading: WD_SHORT[wd] ?? '',
      sub: has ? `${String(new Date(date + 'T00:00:00').getDate()).padStart(2, '0')}.` : '—',
      off: !has,
      segs: has ? segsForDate(date, slots, units, blocks, mode) : slots.map(() => ({
        span: 1, empty: true, seriesId: '', colorKey: '', label: '', date, entries: [],
      })),
    }
  })
  return { monday: week.monday, label: week.label, slots, days }
}

export function weeksFromLessons(lessons: BrockenLesson[]): WeekSlice[] {
  return groupDatesByIsoWeek(uniqueLessonDates(lessons))
}

export const SERIES_COLORS = [
  { bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-800 dark:text-indigo-200', border: 'border-indigo-200 dark:border-indigo-800' },
  { bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-800 dark:text-teal-200', border: 'border-teal-200 dark:border-teal-800' },
  { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-900 dark:text-amber-200', border: 'border-amber-200 dark:border-amber-800' },
  { bg: 'bg-violet-50 dark:bg-violet-950/40', text: 'text-violet-800 dark:text-violet-200', border: 'border-violet-200 dark:border-violet-800' },
  { bg: 'bg-cyan-50 dark:bg-cyan-950/40', text: 'text-cyan-800 dark:text-cyan-200', border: 'border-cyan-200 dark:border-cyan-800' },
  { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-800 dark:text-rose-200', border: 'border-rose-200 dark:border-rose-800' },
  { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-800 dark:text-blue-200', border: 'border-blue-200 dark:border-blue-800' },
  { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-800 dark:text-emerald-200', border: 'border-emerald-200 dark:border-emerald-800' },
] as const

export function colorIndex(key: string, keys: string[]): number {
  const sorted = [...new Set(keys)].sort()
  const i = sorted.indexOf(key)
  return i < 0 ? 0 : i % SERIES_COLORS.length
}
